import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as _ from 'lodash';

export interface IPayment {
  paymentId: string;
  amount: string;
  paidDateAndTime: string;
  paymentStatus: string;
  organization: {
    organizationId: string;
    name: string;
  };
}

export interface IInvoice {
  invoiceId: string;
  systemId: string;
  invoiceDate: string;
  total: string;
  status: string;
  subscriptionPlan: string;
  stripeAccountId: string;
  paidDateAndTime: string;
  paymentId: string;
}

export interface SubscriptionMeta {
  baseValue: string;
  name: string;
  quantity: string;
}
export interface IInvoiceMeta {
  invoiceMetaId: string;
  key: string;
  amount: string;
  quantity: string;
}

export interface IInvoiceDetail {
  systemId?: string;
  invoiceId: string;
  isCorporate?: boolean;
  invoiceDate: string;
  companyName: string;
  customerName: string;
  accountId: string;
  status: string;
  invoiceMeta: IInvoiceMeta[];
  action?: string;
  subscriptionMeta?: SubscriptionMeta;
  paidDateAndTime?: string;
  total?: number;
  transactionId?: string;
}

export interface IGetInvoicesPayload {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: string;
  organizationId?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

export interface IInvoiceSlice {
  componentStatus: { [key: string]: undefined | string | boolean };
  invoices: IInvoice[];
  totalInvoicesCount: number;
  currentPage: number;
  totalPages: number;
  invoiceDetail: IInvoiceDetail;
}

const initialState: IInvoiceSlice = {
  componentStatus: {},
  invoices: [],
  totalInvoicesCount: 0,
  currentPage: 1,
  totalPages: 1,
  invoiceDetail: {
    invoiceId: '',
    invoiceDate: '',
    companyName: '',
    customerName: '',
    accountId: '',
    status: '',
    invoiceMeta: [],
  },
};

const invoiceSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    setComponentStatus(state: IInvoiceSlice, action: PayloadAction<{ [key: string]: undefined | string | boolean }>) {
      state.componentStatus = { ...state.componentStatus, ...action.payload };
    },

    clearComponentStatus(state: IInvoiceSlice, action: PayloadAction<string[] | undefined>) {
      if (action.payload?.length) {
        state.componentStatus = _.omit(state.componentStatus, action.payload);
      } else {
        state.componentStatus = {};
      }
    },

    getInvoicesAsync(_state: IInvoiceSlice, _action: PayloadAction<IGetInvoicesPayload>) {},

    setInvoices(
      state: IInvoiceSlice,
      action: PayloadAction<{ data: any[]; count: number; currentPage: number; totalPages: number }>,
    ) {
      state.invoices = action.payload.data;
      state.totalInvoicesCount = action.payload.count;
      state.currentPage = action.payload.currentPage;
      state.totalPages = action.payload.totalPages;
    },

    resetInvoices(state: IInvoiceSlice) {
      state.invoices = [];
      state.totalInvoicesCount = 0;
      state.currentPage = 1;
      state.totalPages = 1;
    },

    getInvoiceByPaymentIdAsync(_state: IInvoiceSlice, _action: PayloadAction<string>) {},

    setInvoiceByPaymentId(state: IInvoiceSlice, action: PayloadAction<IInvoiceDetail>) {
      state.invoiceDetail.companyName = action.payload.companyName;
      state.invoiceDetail.customerName = action.payload.customerName;
      state.invoiceDetail.invoiceDate = action.payload.invoiceDate;
      state.invoiceDetail.invoiceId = action.payload.systemId || action.payload.invoiceId;
      state.invoiceDetail.status = action.payload.status;
      state.invoiceDetail.invoiceMeta = action.payload.invoiceMeta;
      state.invoiceDetail.subscriptionMeta = action.payload.subscriptionMeta;
      state.invoiceDetail.isCorporate = action.payload.isCorporate;
      state.invoiceDetail.action = action.payload.action;
      state.invoiceDetail.paidDateAndTime = action.payload.paidDateAndTime;
      state.invoiceDetail.total = action.payload.total;
      state.invoiceDetail.transactionId = action.payload.transactionId;
      state.invoiceDetail.accountId = action.payload.accountId;
    },

    downloadInvoiceAsync(_state: IInvoiceSlice, _action: PayloadAction<string>) {},
  },
});

export const { actions: invoiceActions, reducer: invoiceReducer } = invoiceSlice;
